import { getActivePromo } from '@/lib/promoState';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Loader2, Lock, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { trackEvent, getAttribution } from '@/lib/track';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Viva Wallet — Smart Checkout.
 *
 * Standard Viva accounts cannot use in-page Native Checkout v2 tokenization
 * (chargetokens → 403). We use the hosted Smart Checkout order instead.
 *  - `embedded` (WooCommerce + Viva): the hosted card form is shown INLINE in an
 *    iframe (Viva's /web2 page sends no X-Frame-Options/CSP, so it can be framed).
 *    On payment Viva navigates the iframe to /public/viva/return which breaks out
 *    to the success page.
 *  - default (redirect): a Pay button creates the order and redirects the buyer
 *    to Viva's secure page, then back to the success page.
 */
export const VivaPayForm = ({
  items, total, form, hasRequiredCustomerInfo, source, shippingOptionId,
  shippingChosen, accentColor, storeId, storeSlug, currency = 'eur', embedded = false, t,
}) => {
  const [processing, setProcessing] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [iframeUrl, setIframeUrl] = useState('');
  const [iframeHeight, setIframeHeight] = useState(1500);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [waitingPayment, setWaitingPayment] = useState(false);
  const lastOrderKey = useRef('');
  const autoTriedKey = useRef('');
  const txIdRef = useRef('');
  const pollRef = useRef(null);

  // Embedded order amount depends on shipping/total — reset the iframe if they change.
  useEffect(() => {
    setIframeUrl('');
    setIframeLoaded(false);
    lastOrderKey.current = '';
  }, [total, shippingOptionId]);

  // Embedded (WooCommerce + Viva): open the hosted card form automatically as soon
  // as the buyer's required info + shipping are filled — no "Pay" click needed.
  useEffect(() => {
    if (!embedded || iframeUrl || processing) return undefined;
    if (!hasRequiredCustomerInfo || !shippingChosen) return undefined;
    const key = `${total}-${shippingOptionId}`;
    if (autoTriedKey.current === key) return undefined;
    const id = setTimeout(() => {
      autoTriedKey.current = key;
      loadEmbedded({ preventDefault() {} });
    }, 600);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, hasRequiredCustomerInfo, shippingChosen, iframeUrl, processing, total, shippingOptionId]);

  // Poll the Viva order state while the embedded card form is shown.
  useEffect(() => {
    if (!iframeUrl || !txIdRef.current) return undefined;
    setWaitingPayment(true);
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/public/viva/poll/${txIdRef.current}`);
        if (data?.payment_status === 'paid') {
          clearInterval(pollRef.current);
          window.location.href = data.redirect_url || `/checkout/success?pi=${txIdRef.current}`;
        } else if (data?.payment_status === 'failed') {
          clearInterval(pollRef.current);
          setWaitingPayment(false);
          setIframeUrl('');
          setErrMsg(t.err_payment);
        }
      } catch { /* keep polling */ }
    };
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [iframeUrl, t]);

  // Viva's hosted page is taller than a typical viewport; some Viva builds post
  // their content height so we can size the iframe to show everything at once
  // (no inner scrollbar). Falls back to the generous default height otherwise.
  useEffect(() => {
    if (!iframeUrl) return undefined;
    const onMsg = (e) => {
      if (typeof e.origin !== 'string' || !e.origin.includes('viva')) return;
      const d = e.data;
      const h = typeof d === 'number' ? d : (d && (d.height || d.frameHeight || d.contentHeight));
      const n = parseInt(h, 10);
      if (n && n > 400 && n < 4000) setIframeHeight(n + 24);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [iframeUrl]);

  const createOrder = async () => {
    if (!shippingChosen) { toast.error(t.toast_choose_shipping); return null; }
    if (!hasRequiredCustomerInfo) { toast.error(t.toast_incomplete_info); return null; }
    try { trackEvent('payment_attempted', { storeId, slug: storeSlug, amount: total, source }); } catch { /* noop */ }
    const { data } = await axios.post(`${API}/public/viva/create-order`, {
      items,
      customer: {
        email: form.email,
        first_name: form.first_name,
        last_name: form.last_name,
        address1: form.address1,
        city: form.city,
        postal_code: form.postal_code,
        country: (form.country || 'FR').toUpperCase(),
        phone: form.phone || '',
      },
      shipping_option_id: shippingOptionId,
      source,
      currency,
      accent_color: accentColor || '',
      attribution: getAttribution(),
      promo_code: getActivePromo(),
    });
    return data || null;
  };

  const handleError = (e) => {
    const msg = e?.response?.data?.detail || t.err_payment;
    try {
      trackEvent('payment_failed', {
        storeId, slug: storeSlug, amount: total, source,
        failureCode: 'viva_order_error', failureMessage: msg,
      });
    } catch { /* noop */ }
    setErrMsg(msg);
    toast.error(msg);
  };

  // ── Embedded (WooCommerce): load the hosted card form inline in an iframe ──
  const loadEmbedded = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setErrMsg('');
    try {
      const data = await createOrder();
      if (data?.redirect_url) {
        txIdRef.current = data.tx_id;
        setIframeLoaded(false);
        setIframeUrl(data.redirect_url);
        lastOrderKey.current = `${total}-${shippingOptionId}`;
      } else throw new Error(t.err_payment);
    } catch (e2) {
      handleError(e2);
    } finally {
      setProcessing(false);
    }
  };

  // ── Default (redirect): create the order and send the buyer to Viva ──
  const payRedirect = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setErrMsg('');
    try {
      const data = await createOrder();
      if (data?.redirect_url) { window.location.href = data.redirect_url; return; }
      throw new Error(t.err_payment);
    } catch (e2) {
      handleError(e2);
      setProcessing(false);
    }
  };

  const sym = currency === 'usd' ? '$' : (currency === 'gbp' ? '£' : '€');
  const disabled = processing || !shippingChosen;

  if (embedded && iframeUrl) {
    return (
      <div className="space-y-3" data-testid="viva-embedded">
        <div className="relative overflow-hidden rounded-2xl ring-1 ring-black/10 shadow-sm bg-[#F5F5F3]">
          {!iframeLoaded && (
            <div className="absolute inset-0 z-10" data-testid="viva-skeleton" aria-hidden="true">
              <div className="h-40 bg-zinc-900 animate-pulse" />
              <div className="p-5 space-y-4 bg-[#F5F5F3]">
                <div className="h-12 rounded-xl bg-black/10 animate-pulse" />
                <div className="h-12 rounded-xl bg-black/10 animate-pulse" />
                <div className="flex items-center gap-3 pt-2">
                  <div className="h-px flex-1 bg-black/10" />
                  <div className="h-3 w-24 rounded bg-black/10 animate-pulse" />
                  <div className="h-px flex-1 bg-black/10" />
                </div>
                <div className="h-11 rounded-lg bg-black/5 animate-pulse" />
                <div className="grid grid-cols-2 gap-3">
                  <div className="h-11 rounded-lg bg-black/5 animate-pulse" />
                  <div className="h-11 rounded-lg bg-black/5 animate-pulse" />
                </div>
                <div className="h-12 rounded-lg bg-zinc-900/80 animate-pulse mt-2" />
              </div>
            </div>
          )}
          <iframe
            src={iframeUrl}
            title="Viva Wallet"
            data-testid="viva-iframe"
            scrolling="no"
            onLoad={() => setIframeLoaded(true)}
            className="w-full block transition-opacity duration-500"
            style={{ height: iframeHeight, border: 0, opacity: iframeLoaded ? 1 : 0 }}
          />
        </div>
        <p className="text-[11px] text-zinc-400 text-center flex items-center justify-center gap-1.5">
          <Lock className="h-3.5 w-3.5 text-emerald-600" /> {t.secured_viva}
        </p>
        {waitingPayment && (
          <p className="text-xs text-zinc-500 text-center flex items-center justify-center gap-1.5" data-testid="viva-waiting">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t.processing}
          </p>
        )}
      </div>
    );
  }

  const onSubmit = embedded ? loadEmbedded : payRedirect;
  const btnLabel = processing ? (
    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t.processing}</>
  ) : !shippingChosen ? (
    <>{t.choose_shipping_btn}</>
  ) : embedded ? (
    <><Lock className="h-4 w-4 mr-2" /> {t.pay} {total.toFixed(2)} {sym}</>
  ) : (
    <><Lock className="h-4 w-4 mr-2" /> {t.pay} {total.toFixed(2)} {sym}</>
  );

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="viva-pay-form">
      {embedded && (
        <div
          className="rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600 flex items-start gap-2"
          data-testid="viva-embedded-hint"
        >
          <CreditCard className="h-4 w-4 mt-0.5 shrink-0 text-zinc-500" />
          <span>{t.viva_embedded_hint}</span>
        </div>
      )}
      <div
        className="rounded-md border border-zinc-200 bg-zinc-50 p-4 flex items-center justify-between gap-2"
        data-testid="viva-wallet-methods"
      >
        <span className="text-sm text-zinc-700">{t.secure_pay}</span>
        <div className="flex items-center gap-2 text-[11px] font-semibold text-zinc-500">
          <span className="px-2 py-1 rounded bg-white border border-zinc-200">VISA</span>
          <span className="px-2 py-1 rounded bg-white border border-zinc-200">Mastercard</span>
          <span className="px-2 py-1 rounded bg-white border border-zinc-200">AMEX</span>
        </div>
      </div>

      {errMsg && (
        <div data-testid="viva-error" className="text-xs text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">
          {errMsg}
        </div>
      )}

      <Button
        type="submit"
        disabled={disabled}
        data-testid="viva-pay-button"
        style={{ backgroundColor: shippingChosen ? (accentColor || '#0A0A0A') : '#A1A1AA' }}
        className="hidden lg:flex w-full hover:opacity-90 text-white rounded-md h-12 text-base font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed items-center justify-center"
      >
        {btnLabel}
      </Button>
      <p className="hidden lg:block text-[11px] text-zinc-400 text-center">
        {t.secured_viva}
      </p>
      <p className="lg:hidden text-xs text-zinc-500 text-center flex items-center justify-center gap-1.5">
        <Lock className="h-3.5 w-3.5 text-emerald-600" /> {t.secure_pay}
      </p>

      {/* Mobile sticky bottom pay bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-t border-zinc-200 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-[0_-8px_30px_rgba(0,0,0,0.06)]">
        <Button
          type="submit"
          disabled={disabled}
          data-testid="mobile-sticky-pay-button"
          style={{ backgroundColor: shippingChosen ? (accentColor || '#0A0A0A') : '#A1A1AA' }}
          className="w-full hover:opacity-90 text-white rounded-md h-14 text-[17px] font-semibold transition active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {btnLabel}
        </Button>
      </div>
    </form>
  );
};
